import { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Input, Button, Typography, Avatar, List, message as antMessage } from "antd";
import { SendOutlined, UserOutlined } from "@ant-design/icons";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../lib/firebase";
import chatService from "../../lib/chatService";

const { TextArea } = Input;
const { Text } = Typography;

export default function ChatPage() {
  const [user, authLoading] = useAuthState(auth);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const messagesEndRef = useRef(null);

  // Admin user details (you can modify this as needed)
  const ADMIN_USER = {
    id: "6gxjF9e6bxXH9HhyNh8hsRRlFUh2",
    name: "Nellab Support",
    email: "admin@nellab.org"
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize chat with admin
  useEffect(() => {
    if (!user) return;

    const initializeChat = async () => {
      try {
        setLoading(true);
        
        // Get or create chat room with admin
        const chatRoom = await chatService.getOrCreateChatRoom(
          user.uid,
          ADMIN_USER.id,
          {
            [user.uid]: {
              name: user.displayName || user.email?.split('@')[0] || 'User',
              email: user.email
            },
            [ADMIN_USER.id]: {
              name: ADMIN_USER.name,
              email: ADMIN_USER.email
            }
          }
        );

        setChatId(chatRoom.id);

        // Get existing messages
        const existingMessages = await chatService.getChatMessages(chatRoom.id, 100);
        setMessages(existingMessages);

        // Mark existing messages as read
        await chatService.markMessagesAsRead(chatRoom.id, user.uid);

        // Subscribe to real-time messages
        const unsubscribe = chatService.subscribeToChatMessages(chatRoom.id, (updatedMessages) => {
          setMessages(updatedMessages);
          
          // Mark new messages as read
          const unreadMessages = updatedMessages.filter(
            msg => msg.receiverId === user.uid && !msg.isRead
          );
          if (unreadMessages.length > 0) {
            chatService.markMessagesAsRead(chatRoom.id, user.uid);
          }
        });

        return unsubscribe;

      } catch (error) {
        console.error('Error initializing chat:', error);
        antMessage.error('Failed to initialize chat');
      } finally {
        setLoading(false);
      }
    };

    const unsubscribePromise = initializeChat();

    return () => {
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, [user]);

  const sendMessage = async () => {
    if (!input.trim() || !user || !chatId) return;

    setLoading(true);
    try {
      const result = await chatService.sendMessage({
        text: input,
        senderId: user.uid,
        senderName: user.displayName || user.email?.split('@')[0] || 'User',
        senderEmail: user.email,
        receiverId: ADMIN_USER.id,
        chatId: chatId
      });

      if (result.success) {
        setInput("");
      } else {
        antMessage.error(`Failed to send message: ${result.error}`);
      }
    } catch (error) {
      antMessage.error(`Error sending message: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Row justify="center" style={{ padding: 20 }}>
        <Col xs={24} md={18} lg={12}>
          <Card title="Chat With YNO" bordered style={{ height: "80vh" }}>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text>Loading chat...</Text>
            </div>
          </Card>
        </Col>
      </Row>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <Row justify="center" style={{ padding: 20 }}>
        <Col xs={24} md={18} lg={12}>
          <Card title="Chat With YNO" bordered style={{ height: "80vh" }}>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text strong>Please sign in to start chatting with YNO Support</Text>
              <br />
              <Text type="secondary">You need to be logged in to use the chat feature.</Text>
            </div>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row justify="center" style={{ padding: 20 }}>
      <Col xs={24} md={18} lg={12}>
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              <div>
                <Text strong>Chat with YNO Support</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {messages.length > 0 ? 'Online' : 'Connecting...'}
                </Text>
              </div>
            </div>
          } 
          bordered 
          style={{ height: "80vh" }}
        >
          {/* Messages Area */}
          <div
            style={{
              height: "70%",
              overflowY: "auto",
              padding: "10px",
              background: "#f7f7f7",
              borderRadius: 8,
              marginBottom: 10,
              border: "1px solid #e8e8e8",
            }}
          >
            {messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text>No messages yet. Start a conversation with YNO Support!</Text>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    display: "flex",
                    justifyContent: msg.senderId === user.uid ? "flex-end" : "flex-start",
                    marginBottom: 15,
                    alignItems: "flex-start",
                  }}
                >
                  {/* Avatar for received messages */}
                  {msg.senderId !== user.uid && (
                    <Avatar 
                      size="small" 
                      icon={<UserOutlined />} 
                      style={{ marginRight: 8, backgroundColor: '#52c41a' }}
                    />
                  )}
                  
                  <div
                    style={{
                      maxWidth: "70%",
                      padding: "12px 16px",
                      borderRadius: 12,
                      background: msg.senderId === user.uid ? "#1890ff" : "#ffffff",
                      border: msg.senderId !== user.uid ? "1px solid #e8e8e8" : "none",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    }}
                  >
                    {/* Sender name for received messages */}
                    {msg.senderId !== user.uid && (
                      <div style={{ marginBottom: 4 }}>
                        <Text strong style={{ fontSize: "12px", color: "#666" }}>
                          {msg.senderName}
                        </Text>
                      </div>
                    )}
                    
                    {/* Message text */}
                    <span style={{ 
                      color: msg.senderId === user.uid ? "white" : "black",
                      wordBreak: "break-word"
                    }}>
                      {msg.text}
                    </span>
                    
                    {/* Timestamp */}
                    <div style={{ 
                      textAlign: msg.senderId === user.uid ? "right" : "left",
                      marginTop: 4
                    }}>
                      <Text 
                        style={{ 
                          fontSize: "10px", 
                          opacity: 0.7,
                          color: msg.senderId === user.uid ? "rgba(255,255,255,0.7)" : "#999"
                        }}
                      >
                        {formatTime(msg.timestamp)}
                        {msg.senderId === user.uid && (
                          msg.isRead ? " ✓✓" : " ✓"
                        )}
                      </Text>
                    </div>
                  </div>

                  {/* Avatar for sent messages */}
                  {msg.senderId === user.uid && (
                    <Avatar 
                      size="small" 
                      style={{ 
                        marginLeft: 8, 
                        backgroundColor: '#f56a00',
                        fontSize: '12px'
                      }}
                    >
                      {user.displayName?.charAt(0) || user.email?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  )}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <Row gutter={10} align="middle">
            <Col flex={1}>
              <TextArea
                placeholder="Write a message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={2}
                disabled={loading}
                style={{ resize: 'none' }}
              />
            </Col>
            <Col>
              <Button 
                type="primary" 
                icon={<SendOutlined />} 
                onClick={sendMessage}
                loading={loading}
                disabled={!input.trim()}
                style={{ height: 'auto' }}
              >
                Send
              </Button>
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );
}